/**
 * Patient Journey Data
 * Fictional patient stories showcasing the 13 domains of patient experience
 */

export const domains = [
  'Diagnosis & Symptom Onset',
  'Symptoms',
  'Treatment Experience',
  'Healthcare System & Providers',
  'Information & Communication',
  'Technology & Data',
  'Support & Resources',
  'Functioning & Daily Life',
  'Lifestyle & Health Behaviors',
  'Emotional Well-Being',
  'Identity, Background & Culture',
  'Environment & Equity',
  'Research & Clinical Trial Participation'
];

export const patientJourneys = [
  {
    id: 'maya',
    name: 'Maya',
    age: 29,
    condition: 'Lupus',
    location: 'Austin, TX',
    color: '#EBA09B', // coral (brand primary)
    illustration: '/illustrations/maya.jpg',
    teaser: "I was 24 when the fatigue became impossible to ignore.",
    fullIntro: "Maya was a graduate student when her body started sending signals she couldn't decode. What began as exhaustion became a five-year journey of advocacy, adaptation, and ultimately, purpose.",
    timeline: [
      {
        year: 2019,
        domain: 'Diagnosis & Symptom Onset',
        title: 'The Beginning',
        quote: "I thought I was just stressed from grad school. The joint pain, the fatigue—I blamed it on pulling all-nighters. It took three doctors and eight months before someone said the word 'lupus.'",
      },
      {
        year: 2020,
        domain: 'Treatment Experience',
        title: 'Finding Balance',
        quote: "The first medication made me feel like a zombie. The second gave me terrible headaches. By the third try, I learned that finding the right treatment is a conversation, not a prescription.",
      },
      {
        year: 2021,
        domain: 'Emotional Well-Being',
        title: 'The Support I Needed',
        quote: "I joined an online support group at 2 AM during a flare. Those strangers became my lifeline. They understood what my friends couldn't—that some days, showering is a victory.",
      },
      {
        year: 2023,
        domain: 'Research & Clinical Trial Participation',
        title: 'Using My Voice',
        quote: "When I joined my first clinical trial, I finally felt like my experience mattered beyond my own body. I wasn't just a patient—I was helping write the next chapter for everyone with lupus.",
      },
    ]
  },
  {
    id: 'marcus',
    name: 'Marcus',
    age: 52,
    condition: 'Type 2 Diabetes',
    location: 'Detroit, MI',
    color: '#B5C9A8', // sage green (brand)
    illustration: '/illustrations/marcus.jpg',
    teaser: "My diagnosis wasn't a surprise—it was a wake-up call I'd been ignoring.",
    fullIntro: "Marcus knew his family history. His father had diabetes. His grandmother had it too. But knowing and preparing are two different things. His journey became about breaking cycles.",
    timeline: [
      {
        year: 2018,
        domain: 'Diagnosis & Symptom Onset',
        title: 'The Numbers',
        quote: "When my doctor showed me my A1C, I already knew. I'd been avoiding that blood test for two years. Seeing the number made it real in a way I couldn't ignore anymore.",
      },
      {
        year: 2019,
        domain: 'Lifestyle & Health Behaviors',
        title: 'Small Changes',
        quote: "I didn't overhaul everything overnight. I started walking after dinner. Then I cut out soda. Each small win gave me momentum for the next one.",
      },
      {
        year: 2020,
        domain: 'Technology & Data',
        title: 'Data as a Partner',
        quote: "Getting a continuous glucose monitor changed everything. Seeing how my body responded in real-time turned management from guesswork into strategy.",
      },
      {
        year: 2022,
        domain: 'Identity, Background & Culture',
        title: 'Breaking the Cycle',
        quote: "In my family, diabetes was just 'what happens.' I'm determined to be the one who changes that story—for myself and for my kids watching me.",
      },
    ]
  },
  {
    id: 'sarah',
    name: 'Sarah',
    age: 35,
    condition: 'Breast Cancer Survivor',
    location: 'Portland, OR',
    color: '#e8b4b8', // soft rose
    illustration: '/illustrations/sarah.jpg',
    teaser: "At 32, cancer wasn't even on my radar. Then everything changed.",
    fullIntro: "Sarah found the lump during a routine self-exam. What followed was a year that redefined her relationship with her body, her career, and what it means to be 'well.'",
    timeline: [
      {
        year: 2021,
        domain: 'Diagnosis & Symptom Onset',
        title: 'The Discovery',
        quote: "I found it on a Sunday morning. By Thursday, I had a diagnosis. That week felt like a lifetime collapsed into days. Everything I thought I knew about my future became uncertain.",
      },
      {
        year: 2021,
        domain: 'Healthcare System & Providers',
        title: 'Finding My Team',
        quote: "I interviewed three oncologists before choosing mine. Some people thought I was being difficult. I called it being my own advocate. The right fit matters.",
      },
      {
        year: 2022,
        domain: 'Functioning & Daily Life',
        title: 'The New Normal',
        quote: "Chemo brain is real. I went from running projects to forgetting words mid-sentence. Learning to ask for help—and accept it—was harder than any treatment.",
      },
      {
        year: 2023,
        domain: 'Support & Resources',
        title: 'Paying It Forward',
        quote: "Now I'm a peer mentor for newly diagnosed patients. When someone asks 'how did you get through it?' I tell them: one day at a time, and never alone.",
      },
    ]
  },
  {
    id: 'james',
    name: 'James',
    age: 41,
    condition: 'Chronic Migraine',
    location: 'Chicago, IL',
    color: '#a8c5c5', // soft teal
    illustration: '/illustrations/james.jpg',
    teaser: "For years, people told me it was 'just a headache.' It was so much more.",
    fullIntro: "James lost jobs, relationships, and years to a condition many people don't take seriously. His journey is about visibility—making the invisible visible.",
    timeline: [
      {
        year: 2015,
        domain: 'Symptoms',
        title: 'More Than Pain',
        quote: "A migraine isn't a headache. It's losing vision in one eye. It's nausea so bad you can't stand. It's canceling plans you'd looked forward to for months. It's a whole-body experience.",
      },
      {
        year: 2017,
        domain: 'Information & Communication',
        title: 'Learning to Explain',
        quote: "I started keeping a detailed symptom diary—not for me, but so I could finally explain to doctors and employers what was happening. Data gave my pain credibility.",
      },
      {
        year: 2020,
        domain: 'Environment & Equity',
        title: 'Workplace Battles',
        quote: "I needed accommodations: dimmer lights, flexible hours, the ability to work from home during flares. Getting them required documentation, advocacy, and sometimes, lawyers.",
      },
      {
        year: 2023,
        domain: 'Treatment Experience',
        title: 'New Options',
        quote: "The new CGRP medications changed my life. From 20 migraine days a month to 5. But it took eight years and countless failed treatments to get here. Persistence matters.",
      },
    ]
  },
  {
    id: 'elena',
    name: 'Elena',
    age: 67,
    condition: 'Rheumatoid Arthritis',
    location: 'Miami, FL',
    color: '#F5D7AA', // gold (brand)
    illustration: '/illustrations/elena.jpg',
    teaser: "I've lived with RA for 30 years. What I know now could have changed everything.",
    fullIntro: "Elena was diagnosed before the internet, before biologics, before patient communities. Her three decades of experience offer perspective that no textbook can teach.",
    timeline: [
      {
        year: 1994,
        domain: 'Diagnosis & Symptom Onset',
        title: 'A Different Era',
        quote: "In 1994, my doctor handed me a pamphlet and a prescription. That was it. No support groups, no online communities, no one to tell me what to expect. I figured it out alone.",
      },
      {
        year: 2005,
        domain: 'Healthcare System & Providers',
        title: 'Becoming an Expert',
        quote: "After a decade, I knew more about RA than some of my doctors. I learned to bring research to appointments, to question recommendations, to be a partner in my care.",
      },
      {
        year: 2015,
        domain: 'Functioning & Daily Life',
        title: 'Adaptation',
        quote: "My hands don't work like they used to. But I've learned: there's always another way to do things. Jar openers, voice typing, asking for help—adaptation isn't giving up, it's getting creative.",
      },
      {
        year: 2023,
        domain: 'Research & Clinical Trial Participation',
        title: 'Wisdom to Share',
        quote: "I've participated in six clinical trials over the years. Not just for me—but because I want the next generation to have options I didn't. My experience is data that can help.",
      },
    ]
  },
];

export default patientJourneys;
